import classNames from "classnames";
import React, { useCallback, useMemo } from "react";
import { IconNavArrowDown } from "../icons";
import { Link } from "../link";
import { Text } from "../text";
import { NavMenuItem } from "../types";
import { HorizontalMainNavMenuPropsForMenuItems } from "./horizontal-main-nav-menu";
import { CSS_CLASSES } from "./nav-menu-css-classes";
import { useNavMenuContext } from "./use-nav-menu-context";

export interface HorizontalMainNavMenuItemProps extends Omit<NavMenuItem, "label" | "active" | "subMenuItems"> {
  /**
   * This prop can be used to set a nav menu item to be active by default. i.e. when the component renders for the very
   * first time. Thereafter, the internal state management logic within the component will track the active menu item
   * based on user interaction.
   *
   * @default false
   */
  defaultActive?: boolean;

  /**
   * A label for the Nav Menu Item, and optional sub menu items.
   *
   * It takes at least one element as `children` that represents the `label` of the navigation link. This element can
   * be a simple `string` or any arbitrary JSX element. When using a JSX Element as the label, care must be taken not
   * to break the overall layout of the nav menu item and the parent nav menu. Ideally, use this to embellish a text
   * label with small, add-on component like `<Tag>` or `<Badge>`.
   *
   * In addition to the above label element, it can optionally take a set of `<HorizontalMainNavMenuItem>` elements.
   * These will act as the sub-menu under it. These can not have further sub menu items. They must only have a single
   * child element acting as the `label` for the sub menu item.
   */
  children: React.ReactChild | React.ReactChild[];
}

export const HorizontalMainNavMenuItem = ({
  id,
  onClick,
  href,
  defaultActive = false,
  disabled,
  className,
  navMenuItemComponent: NavMenuItemComponent,
  navMenuItemComponentProps,
  children,
  ...restProps
}: HorizontalMainNavMenuItemProps) => {
  const childrenArray = React.useMemo(() => React.Children.toArray(children), [children]);

  const hasSubMenuItems = useMemo(() => childrenArray.length > 1, [childrenArray.length]);

  const { navMenuProps, getMenuItemNode, activeNavMenuPath, updateActiveNavMenuPath } =
    useNavMenuContext<HorizontalMainNavMenuPropsForMenuItems>();

  const menuItemDepth = useMemo(() => getMenuItemNode(id).depth, [getMenuItemNode, id]);

  const handleLinkClick = useCallback(() => {
    updateActiveNavMenuPath(id);
    onClick?.();
  }, [updateActiveNavMenuPath, id, onClick]);

  const handleLinkKeyDown = useCallback<React.KeyboardEventHandler<HTMLAnchorElement>>(
    ({ key }) => {
      if (key === "Enter" || key === " " || key === "Spacebar") {
        handleLinkClick();
      }
    },
    [handleLinkClick]
  );

  const maybeSubMenuIndicator = useMemo(() => {
    if (hasSubMenuItems && menuItemDepth === 1) {
      return <IconNavArrowDown className={CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_ITEM_LINK_LABEL_ICON} />;
    } else {
      return null;
    }
  }, [hasSubMenuItems, menuItemDepth]);

  const maybeSubMenuItems = useMemo(() => {
    if (hasSubMenuItems && menuItemDepth === 1) {
      return <ul>{childrenArray.slice(1)}</ul>;
    } else {
      return null;
    }
  }, [childrenArray, hasSubMenuItems, menuItemDepth]);

  const navMenuItemClassName = useMemo(
    () => classNames(CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_ITEM, className),
    [className]
  );

  const navMenuItemLinkClassName = useMemo(
    () =>
      classNames({
        [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_ITEM_LINK]: menuItemDepth === 1,
        [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_ITEM_LINK_LIGHT]: menuItemDepth === 1 && navMenuProps.variant === "light",
        [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_ITEM_LINK_DARK]: menuItemDepth === 1 && navMenuProps.variant === "dark",
      }),
    [menuItemDepth, navMenuProps.variant]
  );

  const navMenuItemLinkLabelClassName = useMemo(
    () =>
      classNames({
        [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_ITEM_LINK_LABEL]: menuItemDepth === 1,
        [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_ITEM_LINK_LABEL_ACTIVE]:
          menuItemDepth === 1 && activeNavMenuPath.includes(id),
        [CSS_CLASSES.HORIZONTAL_MAIN_NAV_MENU_SUB_ITEM_LINK_LABEL_ACTIVE]:
          menuItemDepth === 2 && activeNavMenuPath.includes(id),
      }),
    [activeNavMenuPath, id, menuItemDepth]
  );

  return (
    <li className={navMenuItemClassName}>
      <Link
        id={id}
        href={href}
        onClick={handleLinkClick}
        {...{ onKeyDown: href ? undefined : handleLinkKeyDown }}
        disabled={disabled}
        underline="never"
        className={navMenuItemLinkClassName}
        linkComponent={NavMenuItemComponent ? <NavMenuItemComponent /> : undefined}
        linkComponentProps={navMenuItemComponentProps}
        {...restProps}
      >
        <Text
          color={menuItemDepth === 1 ? navMenuProps.color : undefined}
          variant={menuItemDepth === 1 ? "label" : "body-long"}
          className={navMenuItemLinkLabelClassName}
        >
          {childrenArray[0]}
          {maybeSubMenuIndicator}
        </Text>
      </Link>
      {maybeSubMenuItems}
    </li>
  );
};
